import {ReactNode, useEffect, useState} from 'react'
import SharedData from '../common/SharedData'
import {IProductModel} from './cors/_models'
import {getProductsList} from './cors/_requests'
import ExportButton from './components/ExportButton'
import Loader from '../common/Loader'
import Paginator from '../common/Paginator'
import ProductsTable from './product-list/ProductsTable'
import ProductsTableFilter from './product-list/ProductsTableFilter'
import {prepareFilters} from '../orders/core/_requests'
import {useSearchParams} from 'react-router-dom'
import {TutorialVideoLink} from '../../tenant/TutorialVideoLink'
import { useIntl } from 'react-intl'
import '../reports/payment-video.css'
import '../../stylesheet/product-list/ProductsList.css'
import ShopScopeBanner from '../../components/ShopScopeBanner'

const ProductsList = () => {
  const {formatMessage} = useIntl();

  const [searchParams, setSearchParams] = useSearchParams()
  const {currentUser, language} = SharedData()
  const [productList, setProductList] = useState<IProductModel[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const [canFilter, setCanFilter] = useState(true)
  const [totalResult, setTotalResult] = useState<number>(0)
  const [perPage, setPerPage] = useState<number>(0)
  const [showVideoHighlight, setShowVideoHighlight] = useState(false)

  useEffect(() => {
    getDate()
  }, [searchParams])
  useEffect(() => {
    const hasSeenVideo = document.cookie
      .split(';')
      .some(item => item.trim().startsWith('dashboard_video='));

    setShowVideoHighlight(!hasSeenVideo);
  }, []);

  const getDate = async () => {
    prepareFilters(searchParams, setSearchParams)
    setIsLoading(true)
    const queryParams = document.location.search
    try {
      const data = await getProductsList(queryParams, currentUser?.token, language)
      if (data) {
        setProductList(data.data.data.products)
        if (data.data.data.total_products){
          setTotalResult(data.data.data.total_products)
          setPerPage(data.data.data.limit)
        }
        else{
          setTotalResult(0)
        }
      } else {
        setProductList([])
        setTotalResult(0)
      }
    } catch (exp) {
      
    }
    setIsLoading(false)
  }

  const resultsData = (): ReactNode => {
    // The range is derived from the rows actually rendered, not from the raw
    // `current_page`: an out-of-range page (a stale bookmark, or a catalog that
    // shrank since the link was shared) would otherwise read "Showing 121–45 of
    // 45" next to an empty table. `perPage`'s 20 fallback matches Paginator's.
    const pageSize = perPage || 20
    const lastPage = Math.max(1, Math.ceil(totalResult / pageSize))
    const currentPage = Math.min(
      lastPage,
      Math.max(1, Number(searchParams.get('current_page')) || 1)
    )
    const shown = productList.length
    const firstResult = shown ? (currentPage - 1) * pageSize + 1 : 0
    const lastResult = shown ? Math.min(totalResult, firstResult + shown - 1) : 0
    return (
      <div className='hsb-pl-table-card'>
        {isLoading && <Loader isFull={true} />}
        <ProductsTable refreshData={getDate} products={productList} />
        <div className='hsb-pl-table-footer'>
          <span>{formatMessage({id: 'products.list.showing'}, {first: firstResult, last: lastResult, total: totalResult})}</span>
          <Paginator totalResult={totalResult} perPage={perPage} />
        </div>
      </div>
    )
  }

  return (
    <div className='hsb-product-list'>
      <ShopScopeBanner />
      <div className='hsb-pl-header'>
        <div>
          <h1>{formatMessage({id: 'prods_list'})}</h1>
          <p>{formatMessage({id: 'products.list.subtitle'})}</p>
        </div>
        <div className='hsb-pl-header-actions'>
          <TutorialVideoLink
            section="products"
            fallback="https://www.youtube.com/watch?v=v62v9sX6hxk"
            target="_blank"
            rel="noopener noreferrer"
            className={`hsb-pl-action hsb-pl-video ${
              showVideoHighlight ? 'video-highlight' : ''
            }`}
            onClick={() => {
              document.cookie = "products_list_video=seen";
              setShowVideoHighlight(false);
            }}
          >
            <i className='far fa-play-circle' aria-hidden='true'></i>
            <span>{formatMessage({id: 'video_link'})}</span>
          </TutorialVideoLink>
          <ExportButton
            queryParams={document.location.search}
            token={currentUser?.token}
            language={language}
          />
          <button
            type='button'
            className='hsb-pl-action hsb-pl-filter-toggle'
            onClick={() => setCanFilter((visible) => !visible)}
            aria-expanded={canFilter}
            aria-controls='products-list-filters'
          >
            <i className='fas fa-sliders-h' aria-hidden='true'></i>
            {formatMessage({id: 'filter'})}
          </button>
        </div>
      </div>

      <div id='products-list-filters' className='hsb-pl-filters' hidden={!canFilter}>
        <ProductsTableFilter />
      </div>

      {resultsData()}
    </div>
  )
}

export default ProductsList
